import { Dispatch, Fragment, SetStateAction, useState } from "react"

// ** Third Party Components
import { useNavigate } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import { ArrowRight2, ClipboardText, CloseCircle, SearchNormal1, Star1 } from "iconsax-reactjs"

// ** Constants
import { tabList } from "@pma/utils/constants"

// ** Helpers
import { cn, renderTime } from "@pma/utils/helpers"

// ** Routes
import { PROJECT_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Theme Components
import { Separator } from "@pma/components/common/separator"
import { Popover, PopoverContent, PopoverTrigger } from "@pma/components/common/popover"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import { RenderFileIcon } from "@pma/components/files/files-view"

interface GlobalSearchPopoverProps {
  activeTab: string
  isLoading: boolean
  searchText: string
  isMoreFetching: boolean
  globalSearchData: any
  setActiveTab: Dispatch<SetStateAction<string>>
  setSearchText: Dispatch<SetStateAction<string>>
  setPaginationData: Dispatch<SetStateAction<any>>
  setResetState: Dispatch<SetStateAction<boolean>>
  setProjectDetails: Dispatch<SetStateAction<any>>
  setIsApiCalling: Dispatch<SetStateAction<boolean>>
  setIsMoreFetching: Dispatch<SetStateAction<boolean>>
}

const GlobalSearchPopover = ({
  isLoading,
  activeTab,
  searchText,
  setActiveTab,
  setSearchText,
  setResetState,
  isMoreFetching,
  setIsApiCalling,
  globalSearchData,
  setPaginationData,
  setIsMoreFetching,
  setProjectDetails,
}: GlobalSearchPopoverProps) => {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const [isClicked, setIsClicked] = useState(false)

  const handleScroll = (e: React.UIEvent) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget
    const bottom = scrollHeight === scrollTop + clientHeight
    if (
      bottom &&
      isMoreFetching == false &&
      globalSearchData[activeTab].counter > globalSearchData[activeTab].data.length
    ) {
      setPaginationData((pre: any) => ({ ...pre, [activeTab]: pre[activeTab] + 1 }))
      setIsMoreFetching(true)
    }
  }

  const renderBreadcrumbsDesktop = (breadcrumbData: any) => {
    return (
      <div className='flex items-center font-[inter] text-[10px] font-normal text-neutral-900'>
        {translate("Project")}
        <ArrowRight2 size={14} />
        {breadcrumbData?.project_groups.length > 0
          ? breadcrumbData?.project_groups.map((segment: any, index: number) => {
              const isLast = index === breadcrumbData?.project_groups.length - 1
              return (
                <Fragment key={index}>
                  <p className='flex font-[inter] text-[10px] font-normal text-neutral-900'>
                    {segment.group_name}
                  </p>
                  <ArrowRight2 size='14' />
                </Fragment>
              )
            })
          : null}
        {breadcrumbData?.project_name}
      </div>
    )
  }

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setIsClicked(false)
          setResetState(true)
        }
      }}
      open={isClicked}
    >
      <PopoverTrigger asChild onClick={() => setIsClicked(true)}>
        <div className='cursor-pointer'>
          <SearchNormal1
            variant={isClicked ? "Bold" : "Linear"}
            className={cn(
              "absolute top-1/2 h-4 w-4 -translate-y-1/2 transform",
              isHebrew ? "right-4" : "left-4",
            )}
          />
          <input
            type='text'
            readOnly
            placeholder={translate("Search")}
            className={cn(
              "hidden w-40 cursor-pointer rounded-full border border-gray-200 bg-neutral-100 py-2 text-sm focus:outline-none md:block",
              isHebrew ? "pl-4 pr-10" : "pl-10 pr-4",
            )}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[650px] md:h-[640px] lg:h-[800px]", isHebrew ? "rtl" : "ltr")}
        align={isHebrew ? "start" : "end"}
      >
        <div className=''>
          <div className='flex justify-between p-4'>
            <div className='flex w-full flex-col gap-2'>
              <div className='relative flex w-full gap-2 rounded-full'>
                <SearchNormal1
                  className={cn(
                    "absolute top-1/2 h-4 w-4 -translate-y-1/2 transform",
                    isHebrew ? "right-4" : "left-4",
                  )}
                />
                <input
                  type='text'
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      setIsApiCalling(true)
                    }
                  }}
                  placeholder={translate("Search")}
                  className={cn(
                    "w-full rounded-full border border-gray-200 bg-gray-50 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500",
                    isHebrew ? "pl-4 pr-10" : "pl-10 pr-4",
                  )}
                />
                {searchText.length ? (
                  <CloseCircle
                    className={cn(
                      "absolute top-1/2 h-4 w-4 -translate-y-1/2 transform cursor-pointer",
                      isHebrew ? "left-4" : "right-4",
                    )}
                    onClick={() => {
                      setSearchText("")
                      setIsApiCalling(true)
                    }}
                  />
                ) : null}
              </div>
              <div className={cn("w-full", isHebrew ? "text-left" : "text-right")}>
                <small>{translate("Press enter for search")}.</small>
              </div>
            </div>
          </div>
          <div className='flex justify-center gap-3 border-b-[1px] border-neutral-100 pb-2'>
            <div className='flex gap-2'>
              {tabList.map((tab: any) => (
                <span
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 px-6 py-2 text-sm font-medium",
                    activeTab === tab.key && "border-indigo-300 bg-indigo-100",
                  )}
                  onClick={() => {
                    setActiveTab(tab.key)
                    setIsApiCalling(true)
                  }}
                >
                  <tab.icon size={16} className='text-neutral-600' />
                  <span>{translate(tab.label)}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div
          className='flex flex-col gap-5 overflow-scroll py-5 md:h-[470px] lg:h-[630px]'
          onScroll={handleScroll}
        >
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className='flex flex-col gap-2 rounded-xl border border-neutral-100 bg-indigo-25 p-4'
              >
                <Skeleton height={20} width={120} />
                <Skeleton height={15} width={`80%`} />
                <Skeleton height={14} width={100} />
                <Skeleton height={10} width={150} />
              </div>
            ))
          ) : globalSearchData[activeTab].data.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-10 text-center text-neutral-500'>
              <SearchNormal1 size={40} className='mb-4 text-neutral-400' />
              <p className='text-lg font-medium'>{translate("No results found")}</p>
              <p className='text-sm'>{translate("Try adjusting your search or filters")}</p>
            </div>
          ) : (
            globalSearchData[activeTab]?.data?.map((item: any, index: number) => {
              return item.type == "file" ? (
                <div className='flex flex-col gap-5 rounded-xl border border-neutral-100 bg-indigo-25 p-4'>
                  <div className='flex flex-col gap-2'>
                    <div className='flex gap-2'>
                      <div>
                        <p className='font-[inter] text-base font-medium text-neutral-800'>
                          {item?.item?._project?.title}
                        </p>
                        <p className='font-[inter] text-sm font-normal text-neutral-800'>
                          {item?.item?._project?.title}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='flex items-center gap-1'>
                        <RenderFileIcon type={item?.item?.type} />
                        <Separator orientation='vertical' />
                        <p className='hidden truncate font-[inter] text-sm font-normal text-neutral-800 md:block'>
                          {item?.item?.name || "Unnamed File"}
                        </p>
                      </div>
                      <div>
                        <p className='font-[inter] text-sm font-normal text-[#1F2633]'>
                          {renderTime(item?.item?.created_at, translate)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.type == "project" ? (
                <div
                  className='flex cursor-pointer flex-col gap-5 rounded-xl border border-neutral-100 bg-indigo-25 p-4'
                  onClick={() => {
                    navigate(`${PROJECT_PAGE_ROUTE}/${item.item.project_id}`, { replace: true })
                    setIsClicked(false)
                    setResetState(true)
                  }}
                >
                  <div className='flex flex-col gap-1'>
                    <div className='flex gap-1'>
                      <div>
                        <ClipboardText className='text-purple-600' />
                      </div>
                      <div className='flex w-full flex-col gap-1'>
                        <div className='flex justify-between'>
                          <p className='font-[inter] text-sm font-normal text-neutral-900'>
                            {item?.item?.title}
                          </p>
                          <div>
                            <Star1
                              size={16}
                              className={cn(
                                item?.item?.pinned_by_user_id.includes(authConfig.id)
                                  ? "fill-black"
                                  : "",
                              )}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setProjectDetails(item?.item)
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className='flex items-center'>
                            {renderBreadcrumbsDesktop(item?.item?.breadcrumbs)}
                          </div>
                          <p className='font-[inter] text-[10px] font-normal text-neutral-900'>
                            {`${translate("Changes")} ${renderTime(item?.item?.last_update, translate)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.type == "note" ? (
                <div
                  key={index}
                  className='flex flex-col gap-5 rounded-xl border border-neutral-100 bg-indigo-25 p-4'
                >
                  <div className='flex flex-col gap-4'>
                    <div className='flex flex-col gap-4'>
                      <div className='flex justify-between'>
                        <div className='flex flex-col gap-1'>
                          <h3 className='text-base font-medium'>Note</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={index}
                  onClick={() => {
                    navigate(
                      `${PROJECT_PAGE_ROUTE}/${item.item.project_id}?query=${item.item.id}`,
                      { replace: true },
                    )
                    setIsClicked(false)
                    setResetState(true)
                  }}
                  className='flex cursor-pointer flex-col gap-5 rounded-xl border border-neutral-100 bg-indigo-25 p-4'
                >
                  <div className='flex flex-col gap-4'>
                    <div className='flex flex-col gap-4'>
                      <div className='flex justify-between'>
                        <div className='flex flex-col gap-1'>
                          <h3 className='text-base font-medium'>Task</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}

          {isMoreFetching && (
            <div className='flex flex-col gap-2 pt-4'>
              <div className='col-span-full flex items-center justify-center py-4'>
                <Skeleton height={20} width={100} />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default GlobalSearchPopover
